import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import TopNav from "@/components/TopNav";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import { Mail, FolderSearch, HardDriveDownload, RefreshCw, Search, Archive, Download } from "lucide-react";

export default function CorreosVault({ user, onLogout }) {
  const navigate = useNavigate();
  const [drive, setDrive] = useState(null);
  const [carpetaQ, setCarpetaQ] = useState("WALMART DATALAKE");
  const [carpetas, setCarpetas] = useState([]);
  const [archivos, setArchivos] = useState([]);
  const [ingestas, setIngestas] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState(null);
  const [cargando, setCargando] = useState(false);

  const [respaldos, setRespaldos] = useState([]);
  const [importaciones, setImportaciones] = useState([]);
  const [enlace, setEnlace] = useState(null);
  const [imp, setImp] = useState({ base_url: "", respaldo_id: "", token: "", incluir_adjuntos: true });
  const esCEO = user?.actuacion === "CEO";
  const API = process.env.REACT_APP_BACKEND_URL;

  const cargarRespaldos = useCallback(async () => {
    try { setRespaldos((await api.get("/correos/respaldo")).data); } catch {}
    try { setImportaciones((await api.get("/correos/respaldo/importaciones")).data); } catch {}
  }, []);

  const enlaceMigracion = async (id) => {
    try { setEnlace((await api.post(`/correos/respaldo/${id}/token`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Error generando enlace"); }
  };

  const importarRespaldo = async () => {
    try {
      await api.post("/correos/respaldo/importar", imp);
      toast.success("Migración iniciada en segundo plano");
      cargarRespaldos();
    } catch (e) { toast.error(e.response?.data?.detail || "Error iniciando migración"); }
  };

  const [errorEstado, setErrorEstado] = useState(null);

  const cargarEstado = useCallback(async () => {
    try {
      const [s, i] = await Promise.all([api.get("/drive/status"), api.get("/correos/ingestas")]);
      setDrive(s.data); setIngestas(i.data); setErrorEstado(null);
    } catch (e) {
      const st = e.response?.status;
      setErrorEstado(st ? `HTTP ${st}: ${e.response?.data?.detail || "error del servidor"}` : "sin respuesta del backend (¿desplegando o reiniciando?)");
    }
    try { setStats((await api.get("/correos/estadisticas")).data); } catch {}
    cargarRespaldos();
  }, [cargarRespaldos]);

  useEffect(() => { if (esCEO) cargarEstado(); }, [esCEO, cargarEstado]);
  useEffect(() => {
    if (!esCEO || drive !== null) return;
    const t = setTimeout(cargarEstado, 4000);
    return () => clearTimeout(t);
  }, [esCEO, drive, errorEstado, cargarEstado]);
  useEffect(() => {
    if (!esCEO) return;
    const t = setInterval(() => {
      if (ingestas.some(i => ["descargando", "procesando"].includes(i.estado))) cargarEstado();
      else if (respaldos.some(r => ["en_cola", "generando"].includes(r.estado)) || importaciones.some(r => ["en_cola", "importando", "verificando"].includes(r.estado))) cargarRespaldos();
    }, 5000);
    return () => clearInterval(t);
  }, [esCEO, ingestas, respaldos, importaciones, cargarEstado, cargarRespaldos]);

  const generarRespaldo = async () => {
    try {
      await api.post("/correos/respaldo/generar");
      toast.success("Generando respaldo en segundo plano…");
      cargarRespaldos();
    } catch (e) { toast.error(e.response?.data?.detail || "Error generando respaldo"); }
  };

  const eliminarRespaldo = async (id) => {
    if (!window.confirm("¿Eliminar los archivos de este respaldo del servidor? (no afecta los correos en la base)")) return;
    try { await api.delete(`/correos/respaldo/${id}`); toast.success("Respaldo eliminado"); cargarRespaldos(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error eliminando respaldo"); }
  };

  const conectarDrive = async () => {
    try {
      const r = await api.get("/drive/connect");
      window.location.href = r.data.authorization_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Error iniciando conexión con Drive"); }
  };

  const buscarCarpetas = async () => {
    setCargando(true);
    try { setCarpetas((await api.get(`/drive/archivos?q=${encodeURIComponent(carpetaQ)}`)).data); setArchivos([]); }
    catch (e) { toast.error(e.response?.data?.detail || "Error listando Drive"); }
    finally { setCargando(false); }
  };

  const abrirCarpeta = async (id) => {
    setCargando(true);
    try { setArchivos((await api.get(`/drive/archivos?folder_id=${id}`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Error listando carpeta"); }
    finally { setCargando(false); }
  };

  const accionIngesta = async (id, accion) => {
    try {
      const r = await api.post(`/correos/ingestas/${id}/${accion}`);
      toast.success(r.data.nota || `Ingesta ${r.data.estado}`);
      cargarEstado();
    } catch (e) { toast.error(e.response?.data?.detail || `Error al ${accion}`); }
  };

  const ingestar = async (f) => {
    try {
      const r = await api.post("/correos/ingestar", { file_id: f.id });
      toast.success(`Ingesta iniciada: ${r.data.zip}`);
      cargarEstado();
    } catch (e) { toast.error(e.response?.data?.detail || "Error iniciando ingesta"); }
  };

  const buscarCorreos = async () => {
    try { setResultados((await api.get(`/correos/buscar?q=${encodeURIComponent(q)}&limit=20`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Error buscando"); }
  };

  const fmtMB = (b) => b ? `${(b / 1048576).toFixed(1)} MB` : "?";

  if (!esCEO) return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <TopNav user={user} onLogout={onLogout} />
      <main className="container mx-auto px-6 py-8"><p className="text-sm text-red-600">Solo el CEO puede acceder al Data Lake de correos.</p></main>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <TopNav user={user} onLogout={onLogout} />
      <main className="container mx-auto px-6 py-8 space-y-6" data-testid="correos-vault-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Mail className="h-6 w-6" /> Correos Vault</h1>
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>Ingesta desde Google Drive de exportaciones de Google Vault (solo CEO)</p>
          </div>
          <Button variant="outline" onClick={() => navigate("/data-lake")} className="h-9 text-xs">← Data Lake</Button>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">1 · Google Drive</CardTitle>
            <CardDescription className="text-xs">
              {drive === null ? (errorEstado ? <span className="text-red-600" data-testid="drive-error">No se pudo consultar el estado: {errorEstado}. Reintentando…</span> : "Cargando…") : !drive.configurado ? "⚠️ Faltan GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET en el backend" :
                drive.conectado ? `Conectado (actualizado ${String(drive.actualizado || "").slice(0, 16)})` : "No conectado"}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex gap-2 flex-wrap">
            <Button onClick={conectarDrive} disabled={!drive?.configurado} className="ls-btn-primary h-9 text-xs" data-testid="drive-conectar-btn">
              <HardDriveDownload className="h-4 w-4 mr-1" /> {drive?.conectado ? "Reconectar Drive" : "Conectar Google Drive"}
            </Button>
            {drive?.conectado && (
              <>
                <input value={carpetaQ} onChange={e => setCarpetaQ(e.target.value)} className="text-xs border rounded-lg px-3 h-9" placeholder="Nombre de carpeta" data-testid="drive-carpeta-input" />
                <Button variant="outline" onClick={buscarCarpetas} disabled={cargando} className="h-9 text-xs" data-testid="drive-buscar-carpetas-btn">
                  <FolderSearch className="h-4 w-4 mr-1" /> Buscar carpetas
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {carpetas.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">2 · Carpetas</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              {carpetas.map(c => (
                <button key={c.id} onClick={() => abrirCarpeta(c.id)} className="block text-xs underline" data-testid={`drive-carpeta-${c.id}`}>📁 {c.name}</button>
              ))}
            </CardContent>
          </Card>
        )}

        {archivos.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">3 · Archivos (elige los ZIP a ingestar)</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {archivos.map(f => (
                <div key={f.id} className="flex items-center justify-between text-xs p-2 rounded-lg" style={{ background: "var(--bg)" }}>
                  <span>{f.mimeType === "application/vnd.google-apps.folder" ? "📁" : "📦"} {f.name} <span style={{ color: "var(--text-muted)" }}>{fmtMB(Number(f.size))} · md5 {String(f.md5Checksum || "").slice(0, 10)}…</span></span>
                  <div className="flex gap-2">
                    {f.mimeType === "application/vnd.google-apps.folder" && <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => abrirCarpeta(f.id)}>Abrir</Button>}
                    {f.name?.toLowerCase().endsWith(".zip") && <Button size="sm" className="h-7 text-[11px] ls-btn-primary" onClick={() => ingestar(f)} data-testid={`ingestar-${f.id}`}>Ingestar</Button>}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base">Ingestas</CardTitle>
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={cargarEstado}><RefreshCw className="h-3 w-3 mr-1" />Actualizar</Button>
          </CardHeader>
          <CardContent className="space-y-2" data-testid="ingestas-list">
            {ingestas.map(i => (
              <div key={i.id} className="text-xs p-2 rounded-lg" style={{ background: "var(--bg)" }}>
                <div className="flex justify-between items-center gap-2">
                  <span className="font-semibold">{i.zip_nombre}</span>
                  <span className="flex items-center gap-2">
                    <span className={i.estado === "completado" ? "text-green-600 font-semibold" : i.estado === "error" ? "text-red-600 font-semibold" : "text-amber-600 font-semibold"} data-testid={`ingesta-estado-${i.id}`}>{i.estado}</span>
                    {["pausado", "error"].includes(i.estado) && <Button size="sm" className="h-6 text-[11px] ls-btn-primary" onClick={() => accionIngesta(i.id, "reanudar")} data-testid={`reanudar-${i.id}`}>Reanudar</Button>}
                    {["en_cola", "descargando", "procesando"].includes(i.estado) && <Button size="sm" variant="outline" className="h-6 text-[11px]" disabled={i.pausa_solicitada} onClick={() => accionIngesta(i.id, "pausar")} data-testid={`pausar-${i.id}`}>{i.pausa_solicitada ? "Pausando…" : "Pausar"}</Button>}
                  </span>
                </div>
                <div style={{ color: "var(--text-muted)" }}>
                  {fmtMB(i.tamano)} · {i.modo === "lectura_remota_por_rangos" ? `leído de Drive ${fmtMB(i.bytes_transferidos || 0)} (${i.peticiones_drive || 0} peticiones)` : `md5 ${i.md5_ok === false ? "❌ NO COINCIDE" : i.md5_ok ? "✓" : "pendiente"}`} ·
                  casillas {i.estado === "completado" ? (i.mboxes?.length ?? 0) : (i.mboxes_completados?.length ?? 0)}/{i.mboxes?.length ?? "?"} ·
                  insertados {i.stats?.insertados ?? 0} · duplicados {i.stats?.duplicados ?? 0} · errores {i.stats?.errores ?? 0} · adjuntos GridFS {i.stats?.adjuntos_gridfs ?? 0} (dedup {i.stats?.adjuntos_dedup ?? 0})
                </div>
                {i.nota && <p className="mt-1 italic" style={{ color: "var(--text-muted)" }}>{i.nota}</p>}
                {i.por_casilla && Object.keys(i.por_casilla).length > 0 && (
                  <div className="mt-1" style={{ color: "var(--text-muted)" }}>
                    {Object.entries(i.por_casilla).map(([c, s]) => <span key={c} className="mr-3">{c}: {s.insertados}{i.result_counts?.[c] ? `/${i.result_counts[c]} (Vault)` : ""}</span>)}
                  </div>
                )}
                {i.errores_detalle?.length > 0 && <p className="text-red-600 mt-1">{i.errores_detalle.slice(0, 3).join(" · ")}</p>}
              </div>
            ))}
            {ingestas.length === 0 && <p className="text-xs" style={{ color: "var(--text-muted)" }}>Sin ingestas aún.</p>}
          </CardContent>
        </Card>

        <Card data-testid="respaldo-card">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><Archive className="h-4 w-4" />Respaldo descargable del Data Lake de correos</CardTitle>
              <CardDescription className="text-xs">Paquete con todos los correos (JSONL) y sus adjuntos (ZIP en partes de ≈512 MB, sha256 por adjunto en el manifest). Las partes se generan al momento de descargar, sin ocupar disco del servidor. Es manual: nada se genera ni se envía solo.</CardDescription>
            </div>
            <Button size="sm" className="h-8 text-xs ls-btn-primary" onClick={generarRespaldo} disabled={respaldos.some(r => ["en_cola", "generando"].includes(r.estado))} data-testid="respaldo-generar-btn">
              <Archive className="h-3 w-3 mr-1" />Generar respaldo
            </Button>
          </CardHeader>
          <CardContent className="space-y-2" data-testid="respaldos-list">
            {respaldos.map(r => (
              <div key={r.id} className="text-xs p-2 rounded-lg" style={{ background: "var(--bg)" }} data-testid={`respaldo-${r.id}`}>
                <div className="flex justify-between items-center gap-2">
                  <span className="font-semibold">Respaldo {r.id} · por {r.iniciado_por}</span>
                  <span className="flex items-center gap-2">
                    <span className={r.estado === "listo" ? "text-green-600 font-semibold" : r.estado === "error" ? "text-red-600 font-semibold" : "text-amber-600 font-semibold"} data-testid={`respaldo-estado-${r.id}`}>
                      {r.estado === "listo" ? "listo" : r.estado}
                    </span>
                    {r.estado !== "generando" && <Button size="sm" variant="outline" className="h-6 text-[11px] text-red-600 border-red-300" onClick={() => eliminarRespaldo(r.id)} data-testid={`respaldo-eliminar-${r.id}`}>Eliminar</Button>}
                    {r.disponible && <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => enlaceMigracion(r.id)} data-testid={`respaldo-enlace-${r.id}`}>Enlace de migración</Button>}
                  </span>
                </div>
                {enlace?.respaldo_id === r.id && (
                  <div className="mt-2 p-2 rounded border text-[11px] space-y-1" style={{ borderColor: "var(--border)", background: "white" }} data-testid={`respaldo-enlace-datos-${r.id}`}>
                    <p>Pega estos 3 datos en la otra instancia (sección "Migrar desde otra instancia"). Válido 24 h; permite solo descargar este respaldo.</p>
                    <p><b>URL origen:</b> <code>{enlace.base_url}</code></p>
                    <p><b>ID respaldo:</b> <code>{enlace.respaldo_id}</code></p>
                    <p><b>Token:</b> <code className="break-all">{enlace.token}</code></p>
                    <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => { navigator.clipboard?.writeText(JSON.stringify({ base_url: enlace.base_url, respaldo_id: enlace.respaldo_id, token: enlace.token })); toast.success("Copiado"); }} data-testid={`respaldo-enlace-copiar-${r.id}`}>Copiar</Button>
                  </div>
                )}
                <div style={{ color: "var(--text-muted)" }}>
                  correos {r.progreso?.correos ?? 0} · adjuntos {r.progreso?.adjuntos ?? 0} ({fmtMB(r.progreso?.bytes || 0)}) · {r.partes?.length ?? 0} archivos · se generan al descargar (sin ocupar disco del servidor); integridad por sha256 de cada adjunto en el manifest
                </div>
                {r.error && <p className="text-red-600 mt-1">{r.error}</p>}
                {r.disponible && (
                  <div className="mt-2 grid gap-1 sm:grid-cols-2" data-testid={`respaldo-partes-${r.id}`}>
                    {r.partes.map(p => (
                      <a key={p.nombre} href={`${API}/api/correos/respaldo/${r.id}/descargar/${p.nombre}`} className="flex items-center justify-between gap-2 px-2 py-1 rounded border hover:bg-white" style={{ borderColor: "var(--border)" }} data-testid={`respaldo-descargar-${r.id}-${p.nombre}`}>
                        <span className="flex items-center gap-1 truncate"><Download className="h-3 w-3 shrink-0" />{p.nombre}</span>
                        <span className="shrink-0" style={{ color: "var(--text-muted)" }}>{p.bytes ? `≈${fmtMB(p.bytes)}` : ""}{p.adjuntos ? ` · ${p.adjuntos} adjuntos` : ""}{p.correos ? ` · ${p.correos} correos` : ""}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {respaldos.length === 0 && <p className="text-xs" style={{ color: "var(--text-muted)" }}>Sin respaldos generados.</p>}

            <div className="mt-4 pt-3 border-t" style={{ borderColor: "var(--border)" }} data-testid="migracion-card">
              <p className="text-xs font-semibold">Migrar desde otra instancia (sin reingestar desde Drive)</p>
              <p className="text-[11px] mb-2" style={{ color: "var(--text-muted)" }}>Copia aquí los 3 datos del "Enlace de migración" generado en la instancia origen. Descarga el respaldo verificando sha256 de cada parte, inserta solo los correos que no existan y, al final, compara conteos por exportación y adjuntos contra el manifest.</p>
              <div className="grid gap-2 sm:grid-cols-3">
                <input value={imp.base_url} onChange={e => setImp({ ...imp, base_url: e.target.value })} placeholder="URL origen (https://…)" className="text-xs border rounded-lg px-3 h-8" data-testid="migracion-base-url" />
                <input value={imp.respaldo_id} onChange={e => setImp({ ...imp, respaldo_id: e.target.value })} placeholder="ID respaldo" className="text-xs border rounded-lg px-3 h-8" data-testid="migracion-respaldo-id" />
                <input value={imp.token} onChange={e => setImp({ ...imp, token: e.target.value })} placeholder="Token" className="text-xs border rounded-lg px-3 h-8" data-testid="migracion-token" />
              </div>
              <div className="flex items-center gap-3 mt-2">
                <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={imp.incluir_adjuntos} onChange={e => setImp({ ...imp, incluir_adjuntos: e.target.checked })} data-testid="migracion-adjuntos-check" /> Incluir adjuntos (GridFS, ~3 GB)</label>
                <Button size="sm" className="h-8 text-xs ls-btn-primary" onClick={importarRespaldo} disabled={!imp.base_url || !imp.respaldo_id || !imp.token || importaciones.some(r => ["en_cola", "importando", "verificando"].includes(r.estado))} data-testid="migracion-importar-btn">Importar</Button>
              </div>
              <div className="space-y-2 mt-2" data-testid="importaciones-list">
                {importaciones.map(m => (
                  <div key={m.id} className="text-xs p-2 rounded-lg" style={{ background: "var(--bg)" }} data-testid={`importacion-${m.id}`}>
                    <div className="flex justify-between">
                      <span className="font-semibold">Migración {m.id} desde {m.base_url}</span>
                      <span className={m.estado === "listo" ? (m.verificacion?.ok ? "text-green-600 font-semibold" : "text-amber-600 font-semibold") : m.estado === "error" ? "text-red-600 font-semibold" : "text-amber-600 font-semibold"} data-testid={`importacion-estado-${m.id}`}>{m.estado}{m.estado === "listo" ? (m.verificacion?.ok ? " · verificado ✓" : " · con diferencias") : ""}</span>
                    </div>
                    <div style={{ color: "var(--text-muted)" }}>
                      correos nuevos {m.progreso?.correos_insertados ?? 0} · ya existentes {m.progreso?.correos_existentes ?? 0} · adjuntos nuevos {m.progreso?.adjuntos_insertados ?? 0} · ya existentes {m.progreso?.adjuntos_existentes ?? 0} · sha incorrectos {m.progreso?.adjuntos_sha_mal ?? 0} · descargado {fmtMB(m.progreso?.bytes_descargados || 0)}{m.progreso?.parte_actual ? ` · ${m.progreso.parte_actual}` : ""}
                    </div>
                    {m.verificacion && (
                      <div className="mt-1" style={{ color: "var(--text-muted)" }} data-testid={`importacion-verificacion-${m.id}`}>
                        correos: manifest {m.verificacion.correos_manifest} / local {m.verificacion.correos_local}
                        {Object.entries(m.verificacion.por_exportacion || {}).map(([e, v]) => <span key={e} className="ml-2">{e}: {v.local}/{v.manifest} {v.ok ? "✓" : "✗"}</span>)}
                        {"adjuntos_manifest" in m.verificacion && <span className="ml-2">· adjuntos {m.verificacion.adjuntos_presentes}/{m.verificacion.adjuntos_manifest} (faltan {m.verificacion.adjuntos_faltantes}) · muestra sha256 {m.verificacion.muestra_sha256_verificados} verificados, {m.verificacion.muestra_sha256_incorrectos} incorrectos</span>}
                      </div>
                    )}
                    {m.error && <p className="text-red-600 mt-1">{m.error}</p>}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Búsqueda {stats ? `(${stats.total} correos · ${stats.canonicos} únicos)` : ""}</CardTitle></CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-3">
              <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && buscarCorreos()} className="text-xs border rounded-lg px-3 h-9 flex-1" placeholder="Buscar en asunto y cuerpo…" data-testid="correos-buscar-input" />
              <Button onClick={buscarCorreos} className="ls-btn-primary h-9 text-xs" data-testid="correos-buscar-btn"><Search className="h-4 w-4" /></Button>
            </div>
            {resultados && (
              <div className="space-y-1" data-testid="correos-resultados">
                <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>{resultados.total} resultados</p>
                {resultados.resultados.map(c => (
                  <div key={c.id + c.cuenta_origen} className="text-xs p-2 rounded-lg" style={{ background: "var(--bg)" }}>
                    <span className="font-semibold">{c.asunto || "(sin asunto)"}</span>
                    <span className="ml-2" style={{ color: "var(--text-muted)" }}>{c.de} → {c.para?.slice(0, 60)} · {String(c.fecha_santiago || "").slice(0, 16)} · {c.cuenta_origen}{c.adjuntos?.length ? ` · 📎${c.adjuntos.length}` : ""}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
